/*
 * svnty-wishlist.js — THE WISHLIST (2026-09-19, Design_Part1).
 * Classic deferred asset. No deps, no app, no login, ASCII only, no console.
 *
 * PARENT: the sibling's assets/sib-wishlist.js (the sibling store's theme). The MODEL is
 * ported line-for-line in shape; the interaction is re-skinned to SVNTY's order — a heart is a
 * TOGGLE, not a list picker, so the sibling's save sheet, named lists, rename/delete and undo strip are
 * not here. The storage envelope keeps the sibling's shape so named lists can land later without a
 * migration, and `v` IS read on every load (see normalise).
 *
 * WHY IT CANNOT BREAK (the sibling's laws, kept)
 *  - ONE source of truth in memory; localStorage is a MIRROR. Every read comes from memory; every
 *    write goes through commit(), which coalesces one disk write and one announcement per frame.
 *    Private mode, a full quota or blocked storage simply mean the wishlist lives for the session —
 *    and the shopper is TOLD once, because a count that resets on reload with no explanation is
 *    worse than a sentence (R2-34).
 *  - Corrupt or foreign bytes are quarantined (svnty:wishlist:quarantine) and the store starts clean
 *    instead of crashing every heart on the page.
 *  - Mutations are idempotent; items are keyed (handle || pid) and deduped on every load.
 *  - Two sync paths are BOTH required: same tab through the `svnty:wishlist:change` CustomEvent,
 *    other tabs through the native `storage` event (which never fires locally). the sibling JS-57.
 *  - A double tap on a heart is ONE action (350 ms window). the sibling sib-wishlist.js:646.
 *  - `[data-svnty-wishlist-count]` is the ONLY selector the counts are written to. A copy of the mark
 *    without that attribute diverges silently — the sibling's paid-for bug.
 *  - Storage failure is not "fixed": the nudge simply never shows. TRAPS IOS-18.
 *
 * SVNTY'S OWN CORRECTION TO THE PARENT
 *  the sibling cached /products/<handle>.js with no TTL, so a size that sold out mid-visit still read as
 *  available (the sibling JS-22). Here every saved item carries a SNAPSHOT taken at save time (title,
 *  image, price, compare, url) so the page paints instantly and works with no network at all, and the
 *  live product is then fetched to correct price and availability. The dev proxy answers
 *  /products/<handle>.js with 401/502 (`shopify theme dev` does not proxy the .js endpoints), so the
 *  snapshot path is the one that must always be right.
 *
 * ══ THE VERIFIED-SNAPSHOT LAW (refutation round 2, R2-5) ══
 *  NOTHING WITHOUT A TITLE MAY LIVE IN THE STORE. Round 2 measured a share link fabricating
 *  products: `?w=` wrote {handle, url} with no snapshot, and the page then printed a title invented
 *  from the handle ("army knit long sleeve polo") next to money(0) — "EGP 0.00" — as a real card with
 *  a real link. The fix is structural, not cosmetic: cleanItem() REJECTS an item with no title, so a
 *  snapshot-less entry cannot be stored, cannot be counted and cannot be rendered. A share link now
 *  FETCHES each handle first and stores the product it got back (mergeFromUrl); a handle that does
 *  not resolve is never mentioned again. A card is therefore never drawn from anything but a real
 *  product — either the snapshot Liquid wrote on the heart, or the JSON the store answered with.
 *  A product that is deleted or unpublished after being saved answers 404/410 on revalidation and is
 *  DROPPED from the wishlist (R2-7) — 404 is definitive; offline, 401 and 502 are not, and those
 *  leave the snapshot card standing.
 *
 * ══ THE VARIANT LAW (wave 3, W-1 / W-2 / W-3 / W-4 / W-5) ══
 *  A MENSWEAR WISHLIST THAT FORGETS THE SIZE SELLS THE WRONG SIZE. The store used to key each item on
 *  the HANDLE and store no variant at all, so "Add to bag" on a wishlist card posted
 *  firstAvailableVariant() — a man saved an M and was sold an S — and every colourway of one piece
 *  collapsed into one card painted with the product's default image and its minimum price.
 *  Each saved item now carries `vid` (the variant the shopper was actually looking at) and `opts`
 *  (that variant's own option line, e.g. "Olive / M"), and the KEY is the variant, so M and L are two
 *  cards. The PDP heart follows the size picker: PUB_SUB_EVENTS.variantChange rewrites the heart's
 *  snapshot attributes, so what is saved is what is on screen.
 *  P17-26 (refuter R2 P3-8): the page's own card (its three states and its ADD TO BAG) is retired with the page
 *  renderer: the wishlist grid is THE CARD (sections/svnty-wishlist.liquid), fetched on the saved colour, and its add
 *  is the card's own quick add.
 *  THE HEART still reads the PRODUCT, not the variant: a piece with any size saved shows as saved, and
 *  a second tap removes the size on screen (else the newest size saved), so the toggle never lies.
 *
 * CONTRACTS (other files depend on these)
 *  window.SvntyWishlist { state has hasProduct count items add remove restore toggle handles
 *    storageAvailable }
 *  localStorage 'svnty:wishlist' = { v, lists:[{id,name,items:[{pid,handle,vid,opts,avail,title,image,img2,price,compare,url,ts}]}], active }
 *  CustomEvent 'svnty:wishlist:change' on document, detail = { count, items } (a frozen copy — never
 *    the live state object, which a listener could mutate behind the store's back: R2-35)
 *  <svnty-wishlist-toggle> + button[aria-pressed] · [data-svnty-wishlist-count] · [data-svnty-wl-count]
 *  [data-svnty-wl-share]
 *  [data-svnty-wl-share-field] (W-9: the link itself, for the day the clipboard refuses)
 *  <script type="application/json" data-svnty-wl-strings> (sections/svnty-wishlist.liquid — the page's
 *    own words; `window.svntyStrings` still wins wherever a `svnty.*` locale key exists, so the day
 *    these four move into locales/*.json nothing in this file changes)
 *  share URL '?w=<handle>,<handle>' verified on load, then stripped from the address bar
 */
(function () {
  'use strict';
  if (window.__svntyWishlistInit) return;
  window.__svntyWishlistInit = true;

  /* WORDS (H4): every sentence comes from `window.svntyStrings`, rendered by Liquid from
     `locales/*.json` in snippets/svnty-head.liquid. No English literal lives in this file.
     `LIST_NAME` below is a STORAGE key's value, never shown to a shopper — it stays literal so a
     translation can never change what is already written in a visitor's localStorage. */
  var STR = window.svntyStrings || {};

  /* THE PAGE'S OWN WORDS (wave 3). The four sentences THE VARIANT LAW and the Undo strip need do not
     exist in `svnty.*` yet, and tonight's law is "schema settings with defaults, no locale edits".
     They ride a JSON island the wishlist section prints, and `window.svntyStrings` is still read
     FIRST — the day the keys land in locales/*.json the island becomes dead weight, not a conflict. */
  var PAGE_STR = null;
  function readPageStrings() {
    if (PAGE_STR) return PAGE_STR;
    PAGE_STR = {};
    var island = document.querySelector('[data-svnty-wl-strings]');
    if (!island) return PAGE_STR;
    try {
      var parsed = JSON.parse(island.textContent || '{}');
      if (parsed && typeof parsed === 'object') PAGE_STR = parsed;
    } catch (e) {}
    return PAGE_STR;
  }

  /* R2-45: `String.prototype.replace` with a STRING pattern replaces the first occurrence only, and
     a value containing `$&`, `` $` ``, `$'` or `$1` is read as a replacement pattern — a shopper's
     own product title or search term can carry those. split/join has no pattern language at all. */
  function fillIn(template, token, value) {
    return String(template || '').split(token).join(String(value));
  }
  function fill(key, token, value) {
    return fillIn(STR[key], token, value);
  }

  /* R2-44: plurals are a LOCALE shape. The bridge emits one rendered string per CLDR category, and
     the browser's own Intl.PluralRules picks the category for this document's language — so Arabic's
     six categories are a locale edit, not a rewrite. No `=== 1` ternary lives in this file. */
  var pluralRules = null;
  function pluralCategory(count) {
    try {
      if (!pluralRules) {
        pluralRules = new Intl.PluralRules(document.documentElement.lang || 'en');
      }
      return pluralRules.select(count);
    } catch (e) {
      return count === 1 ? 'one' : 'other';
    }
  }
  function plural(key, count) {
    var group = STR[key];
    if (!group || typeof group !== 'object') return '';
    var form = group[pluralCategory(count)] || group.other || group.one || '';
    return String(form).split('[count]').join(String(count));
  }

  var KEY = 'svnty:wishlist';
  var QUARANTINE = 'svnty:wishlist:quarantine';
  var NUDGE_KEY = 'svnty:wishlist:nudge';
  var EVT = 'svnty:wishlist:change';
  var VERSION = 1;
  var MAX_ITEMS = 200;
  var MAX_IMPORT = 50; /* one share link cannot ask for more requests than this */
  var LIST_NAME = 'Wishlist';
  var FETCH_MS = 8000;
  var NUDGE_AT = 3;

  /* R2-47: THE ROOT IS NOT '/'. On a locale- or market-prefixed path Shopify serves the store from
     `/eg/` or `/en-eg/`, and a hardcoded '/pages/wishlist' or '/products/x.js' 404s there. Liquid
     already publishes `window.routes.cart_url` (layout/theme.liquid), which carries the prefix, so
     the root is read from it instead of guessed. No new Liquid bridge is needed for this. */
  function root() {
    var cart = (window.routes && window.routes.cart_url) || '/cart';
    return String(cart).replace(/\/cart\/?$/, '') + '/';
  }
  function pageUrl() {
    return root() + 'pages/wishlist';
  }

  /* ======================================================================= *
   * STORE — memory first, storage as a mirror
   * ======================================================================= */
  function uid() {
    return 'l' + Date.now().toString(36) + Math.floor(Math.random() * 1e6).toString(36);
  }
  function str(x) {
    return x == null ? '' : String(x);
  }
  /* THE VARIANT LAW's two keys. The ITEM key is the variant (so M and L are two cards); the PRODUCT
     key is what a heart reflects (a piece with any size saved reads as saved). Both are PREFIXED:
     a Shopify variant id is a bare number and a handle generated from a numeric title is a bare
     number too, so an unprefixed key could in principle collide across the two spaces. */
  function itemKey(it) {
    return it.vid ? 'v' + str(it.vid) : 'p' + (it.handle ? str(it.handle) : str(it.pid));
  }
  function productKey(it) {
    return 'p' + (it && it.handle ? str(it.handle) : str(it && it.pid));
  }

  /* R2-36: A STORED URL REACHES AN href. localStorage is the shopper's own, but it is also the one
     place a hostile page on the same origin, a bookmarklet or a synced backup can write — and
     `javascript:...` in `url` would become a live link on this page. Only a same-origin ROOT-RELATIVE
     path is accepted; anything else (a scheme, a protocol-relative `//host`, a bare word) is
     discarded and the canonical /products/<handle> is used instead. */
  function safePath(value, handle) {
    var url = str(value).trim();
    var fallback = handle ? root() + 'products/' + handle : '';
    if (!url) return fallback;
    if (url.charAt(0) !== '/' || url.charAt(1) === '/') return fallback;
    if (/[\u0000-\u001f]/.test(url)) return fallback;
    return url;
  }

  /* THE VERIFIED-SNAPSHOT LAW lives here: no title, no item. */
  function cleanItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var handle = str(raw.handle);
    var pid = str(raw.pid || raw.id);
    if (!handle && !pid) return null;
    var title = str(raw.title).trim();
    if (!title) return null;
    return {
      pid: pid,
      handle: handle,
      /* W-1: the variant the shopper was looking at. A Shopify variant id is digits and nothing
         else; anything that is not (a mangled attribute, a foreign backup) is dropped rather than
         stored, because it would be posted to /cart/add.js verbatim. Blank = "pick a size". */
      vid: /^[0-9]{1,20}$/.test(str(raw.vid)) ? str(raw.vid) : '',
      /* W-4: that variant's own option line, e.g. "Olive / M". Liquid never sends it for a product
         whose only variant is the default one, so a single-size piece prints the blank seat. */
      opts: str(raw.opts).trim().slice(0, 80),
      /* W-5: the SAVED size's availability at save time. Without it a sold-out size can only be
         discovered by fetching the product — and on the dev proxy (429 + a bot check on every
         /products/*.js) and on a bad minute that fetch never lands, so the card would offer to sell
         a size that is gone. `true` is the default for everything legacy or malformed: the card then
         behaves exactly as it did before this law, and the live fetch still corrects it. */
      avail: str(raw.avail) === 'false' || raw.avail === false ? false : true,
      title: title,
      image: str(raw.image),
      img2: str(raw.img2),
      price: Number(raw.price) || 0,
      compare: Number(raw.compare) || 0,
      url: safePath(raw.url, handle),
      ts: Number(raw.ts) || Date.now()
    };
  }
  function cleanList(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var seen = {};
    var items = [];
    (Array.isArray(raw.items) ? raw.items : []).forEach(function (x) {
      var it = cleanItem(x);
      if (!it) return;
      var k = itemKey(it);
      if (seen[k]) return;
      seen[k] = true;
      if (items.length < MAX_ITEMS) items.push(it);
    });
    return { id: str(raw.id) || uid(), name: str(raw.name) || LIST_NAME, items: items };
  }
  /* normalise anything into a valid state; never throws.
     R2-35: `v` IS READ. Three shapes reach this function and the envelope's version is what tells
     them apart, which is the whole point of writing it:
       · a bare ARRAY  — the pre-envelope build (v absent, treated as 0)
       · v === VERSION — this build's single list
       · v  >  VERSION — a store written by a LATER, multi-list build, on a shopper who has since
         gone back to this theme version. Its extra lists are folded into one in order rather than
         discarded, which is the migration the header's claim always promised. */
  function normalise(parsed) {
    var version = 0;
    var list = null;
    if (Array.isArray(parsed)) {
      list = cleanList({ name: LIST_NAME, items: parsed });
    } else if (parsed && typeof parsed === 'object' && Array.isArray(parsed.lists)) {
      version = Number(parsed.v) || 0;
      for (var i = 0; i < parsed.lists.length && !list; i++) list = cleanList(parsed.lists[i]);
      if (list && version > VERSION) {
        for (var j = 1; j < parsed.lists.length; j++) {
          var extra = cleanList(parsed.lists[j]);
          if (!extra) continue;
          extra.items.forEach(function (it) {
            for (var k = 0; k < list.items.length; k++) if (itemKey(list.items[k]) === itemKey(it)) return;
            if (list.items.length < MAX_ITEMS) list.items.push(it);
          });
        }
      }
    }
    if (!list) list = { id: uid(), name: LIST_NAME, items: [] };
    return { v: VERSION, lists: [list], active: list.id };
  }

  var storageOk = true;
  var storageTold = false;
  function storageFailed() {
    /* R2-34: a WRITE that throws is the failure that matters — quota exceeded, or Safari's private
       mode, which reads happily and refuses to write. Round 2 measured the silent divergence:
       memory says N this session, the mirror still says N-1, and the count changes by itself on
       reload. The state is recorded AND said once, then never mentioned again. */
    if (!storageOk) return;
    storageOk = false;
    if (storageTold) return;
    storageTold = true;
    toast(STR.wl_no_storage || '');
  }
  function readDisk() {
    var raw = null;
    try {
      raw = localStorage.getItem(KEY);
    } catch (e) {
      storageOk = false;
      return normalise(null);
    }
    if (raw == null) return normalise(null);
    try {
      return normalise(JSON.parse(raw));
    } catch (e) {
      /* corrupt: quarantine the bytes, start clean, keep every heart working */
      try {
        localStorage.setItem(QUARANTINE, raw);
      } catch (e2) {}
      try {
        localStorage.removeItem(KEY);
      } catch (e3) {}
      return normalise(null);
    }
  }
  /* THE HANDLE REMAP (R109 step 2): items saved on a pre-merge listing follow the merged product; their
     variant id belongs to an archived listing, so it is dropped and the item re-resolves by handle + options. */
  function remapState(st) {
    var m = window.SVNTY_HANDLE_REMAP;
    if (!m || !st || !st.lists) return st;
    st.lists.forEach(function (l) {
      (l.items || []).forEach(function (it) {
        if (it && typeof it.handle === 'string' && typeof m[it.handle] === 'string') {
          it.handle = m[it.handle];
          it.url = '/products/' + it.handle;
          it.vid = 0;
          it.pid = 0;
        }
      });
    });
    return st;
  }
  var state = remapState(readDisk());
  function list() {
    return state.lists[0];
  }

  var writeQueued = false;
  function writeDisk() {
    writeQueued = false;
    if (!storageOk) return;
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) {
      storageFailed();
    }
  }
  function snapshot() {
    /* a frozen copy: a listener can read it and cannot reach into the store through it */
    return list().items.map(function (it) {
      var copy = {};
      Object.keys(it).forEach(function (k) {
        copy[k] = it[k];
      });
      return Object.freeze ? Object.freeze(copy) : copy;
    });
  }
  function announce() {
    try {
      document.dispatchEvent(
        new CustomEvent(EVT, { detail: { count: WL.count(), items: snapshot() } })
      );
    } catch (e) {}
  }
  var announceQueued = false;
  /* every mutation ends here: one storage write + one announce per frame, however many taps */
  function commit() {
    if (!writeQueued) {
      writeQueued = true;
      Promise.resolve().then(writeDisk);
    }
    if (!announceQueued) {
      announceQueued = true;
      window.requestAnimationFrame(function () {
        announceQueued = false;
        announce();
      });
    }
  }
  function indexOfKey(key) {
    var items = list().items;
    for (var i = 0; i < items.length; i++) if (itemKey(items[i]) === key) return i;
    return -1;
  }
  /* the NEWEST saved size of a piece — what a second tap on a pressed heart takes back */
  function indexOfProduct(key) {
    var items = list().items;
    var best = -1;
    for (var i = 0; i < items.length; i++) {
      if (productKey(items[i]) !== key) continue;
      if (best < 0 || (items[i].ts || 0) > (items[best].ts || 0)) best = i;
    }
    return best;
  }

  var WL = {
    state: function () {
      return state;
    },
    items: function () {
      return list().items;
    },
    count: function () {
      return list().items.length;
    },
    /* read by the nudge and by the heart's failure path — the one honest answer to
       "did that tap survive a reload?" (R2-35: this is no longer a method with no callers) */
    storageAvailable: function () {
      return storageOk;
    },
    /* an EXACT saved variant */
    has: function (key) {
      return indexOfKey(str(key)) > -1;
    },
    /* W-1: any size of this piece. The heart asks this one — "is this piece saved?" is the shopper's
       question, not "is the 42 saved?". `raw` is a handle or a product id. */
    hasProduct: function (raw) {
      var key = str(raw);
      if (!key) return false;
      var items = list().items;
      for (var i = 0; i < items.length; i++) {
        if (str(items[i].handle) === key || str(items[i].pid) === key) return true;
      }
      return false;
    },
    /* true when it saved · false when it was already there or the snapshot was rejected ·
       'full' when the cap is what stopped it, so the caller can SAY so (R2-34) */
    add: function (raw) {
      var it = cleanItem(raw);
      if (!it) return false;
      if (indexOfKey(itemKey(it)) > -1) return false;
      if (list().items.length >= MAX_ITEMS) return 'full';
      list().items.push(it);
      commit();
      return true;
    },
    /* W-7: a removal HANDS BACK what it took — the item and the index it sat at — so the caller can
       offer Undo. `false` when there was nothing to remove. */
    remove: function (key) {
      var i = indexOfKey(str(key));
      if (i < 0) return false;
      var removed = list().items.splice(i, 1)[0];
      commit();
      return { item: removed, index: i };
    },
    /* W-7: put it back where it was. Idempotent — a double Undo is one Undo. */
    restore: function (item, index) {
      var it = cleanItem(item);
      if (!it) return false;
      if (indexOfKey(itemKey(it)) > -1) return false;
      if (list().items.length >= MAX_ITEMS) return false;
      var at = Math.max(0, Math.min(Number(index) || 0, list().items.length));
      list().items.splice(at, 0, it);
      commit();
      return true;
    },
    /* returns true when it saved, false when it unsaved, 'full' at the cap, null when it could not act.
       THE VARIANT LAW: the exact variant wins; failing that a pressed heart takes back the NEWEST size
       of this piece, because that is the state the heart is showing. */
    toggle: function (raw) {
      var it = cleanItem(raw);
      if (!it) return null;
      if (indexOfKey(itemKey(it)) > -1) {
        WL.remove(itemKey(it));
        return false;
      }
      var p = indexOfProduct(productKey(it));
      if (p > -1) {
        WL.remove(itemKey(list().items[p]));
        return false;
      }
      var added = WL.add(it);
      return added === true ? true : added === 'full' ? 'full' : null;
    },
    /* THE SHARE LINK carries PRODUCTS, not variants ('?w=' is a list of handles and always was), so
       two saved sizes of one piece are one handle here. Deduped in save order. */
    handles: function () {
      var seen = {};
      var out = [];
      list().items.forEach(function (it) {
        var h = str(it.handle);
        if (!h || seen[h]) return;
        seen[h] = true;
        out.push(h);
      });
      return out;
    }
  };
  window.SvntyWishlist = WL;

  /* other tabs write the mirror; this tab reloads it (the storage event never fires locally) */
  window.addEventListener('storage', function (e) {
    if (e.key !== KEY && e.key !== null) return;
    state = readDisk();
    announce();
  });

  /* ======================================================================= *
   * HELPERS
   * ======================================================================= */
  function h(tag, attrs, kids) {
    var el = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        var v = attrs[k];
        if (v == null || v === false) return;
        if (k === 'text') el.textContent = v;
        else if (k === 'class') el.className = v;
        else if (k === 'html') el.innerHTML = v; /* trusted inline SVG only — never product data */
        else el.setAttribute(k, v === true ? '' : v);
      });
    }
    (kids || []).forEach(function (c) {
      if (c == null) return;
      el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return el;
  }
  /* ======================================================================= *
   * THE TOAST — one strip, bottom, safe-area padded, never a dialog
   * R2-48: it now carries a DISMISS, and it never auto-hides out from under the keyboard user
   * whose focus is inside it — the old 4 s timer could remove the focused action link and drop
   * focus to <body>.
   * ======================================================================= */
  var toastEl = null;
  var toastTimer = null;
  var toastToken = 0;
  var TOAST_MS = 4000;

  function hideToast() {
    window.clearTimeout(toastTimer);
    toastTimer = null;
    if (!toastEl) return;
    /* never yank the node the shopper is standing on */
    if (toastEl.contains(document.activeElement)) {
      toastTimer = window.setTimeout(hideToast, TOAST_MS);
      return;
    }
    toastEl.hidden = true;
  }
  function toast(message, action) {
    if (!message) return;
    if (!toastEl) {
      toastEl = h('div', { class: 'svnty-toast svnty-wl-toast', role: 'status', 'aria-live': 'polite' });
      document.body.appendChild(toastEl);
      toastEl.addEventListener('click', function (e) {
        if (e.target.closest('[data-svnty-toast-close]')) hideToast();
      });
    }
    toastEl.textContent = '';
    toastEl.appendChild(h('span', { class: 'svnty-wl-toast__msg', text: message }));
    /* W-7: the action is EITHER a link (View bag, Open wishlist) OR a one-shot callback (Undo). The
       callback is bound to THIS toast: a second toast replaces the strip and the stale Undo can no
       longer fire, so an Undo never puts back the wrong piece. */
    if (action && action.label && action.onAction) {
      var token = ++toastToken;
      var act = h('button', { type: 'button', class: 'svnty-wl-toast__action svnty-wl-toast__action--btn', text: action.label });
      act.addEventListener('click', function () {
        if (token !== toastToken) return;
        toastEl.hidden = true;
        window.clearTimeout(toastTimer);
        toastTimer = null;
        action.onAction();
      });
      toastEl.appendChild(act);
    } else if (action && action.label && action.href) {
      toastToken++;
      toastEl.appendChild(h('a', { class: 'svnty-wl-toast__action', href: action.href, text: action.label }));
    } else {
      toastToken++;
    }
    toastEl.appendChild(
      h('button', {
        type: 'button',
        class: 'svnty-wl-toast__close',
        'data-svnty-toast-close': true,
        'aria-label': STR.close || '',
        html:
          ((window.SVNTY && SVNTY.iconHTML) ? SVNTY.iconHTML('close', 14, 1.5) : '')  /* THE ICON LAW (D11-3) */
      })
    );
    toastEl.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(hideToast, (action && action.ms) || TOAST_MS);
  }

  /* THE NUDGE — after the third save, once ever. Storage failure means it never shows, by design. */
  function maybeNudge() {
    if (WL.count() < NUDGE_AT) return;
    if (!WL.storageAvailable()) return;
    try {
      if (localStorage.getItem(NUDGE_KEY)) return;
      localStorage.setItem(NUDGE_KEY, '1');
    } catch (e) {
      return;
    }
    toast(STR.wl_nudge || '', { label: STR.wl_open || '', href: pageUrl() });
  }

  /* ======================================================================= *
   * THE HEART — a toggle, not a picker
   * ======================================================================= */
  /* A-04 (D5-1): a deep link counts as a chosen size only as the page ARRIVED; Trade's updateURL() writes ?variant= on every
     pick, a colour pick too, so the live URL would save the default size after a colour tap */
  var ARRIVED_WITH_VARIANT = false;
  try { ARRIVED_WITH_VARIANT = /[?&]variant=\d+/.test(window.location.search); } catch (e) { /* no location */ }
  function sizeChosen(el) {
    if (!el.classList.contains('svnty-heart--pdp')) return true;          /* cards never carry a size; the attribute is absent anyway */
    if (el.getAttribute('data-single-variant') === 'true') return true;   /* one size only: nothing to choose */
    if (el.getAttribute('data-svnty-size-picked') === '1') return true;
    return ARRIVED_WITH_VARIANT;
  }
  function readSnapshot(el) {
    return {
      pid: el.getAttribute('data-product-id'),
      handle: el.getAttribute('data-product-handle'),
      /* THE VARIANT LAW: present on the PDP (and kept current by the picker, below), absent on a
         collection card — where no size has been chosen and inventing one is the bug. */
      /* D4-7 (the Part3 audit: "the wishlist heart should not save a default size"): on the PDP the server-rendered
         heart carries the first available variant, which is Shopify's default size, not a choice. A size travels
         with the snapshot only when the shopper picked one (the size picker fired) or arrived on a ?variant= link;
         otherwise the card says PICK A SIZE, which is the truth. */
      vid: sizeChosen(el) ? el.getAttribute('data-variant-id') : '',
      opts: sizeChosen(el) ? el.getAttribute('data-variant-opts') : '',
      avail: el.getAttribute('data-variant-available'),
      title: el.getAttribute('data-title'),
      image: el.getAttribute('data-image'),
      img2: el.getAttribute('data-image-2x'),
      price: el.getAttribute('data-price'),
      compare: el.getAttribute('data-compare'),
      url: el.getAttribute('data-url')
    };
  }

  /* ── THE HEART FOLLOWS THE SIZE PICKER ──────────────────────────────────────
     Trade publishes PUB_SUB_EVENTS.variantChange at the END of renderProductInfo with the variant's
     own JSON (assets/global.js). Without this the PDP heart would carry the FIRST variant's id for
     the whole visit and save an S while an M is on screen — W-1 in one attribute. The heart lives in
     the title block, outside every region Trade swaps, so it survives the re-render and only its
     attributes need rewriting. `product.url` is rewritten with ?variant= too, so the wishlist card's
     link opens the PDP on the size that was saved. */
  /* Shopify's variant JSON gives a raw CDN src ('...jpg?v=1699'); Liquid's image_url gives one with
     `&width=`. Either way the width is REPLACED, never appended twice, and the separator is decided
     from the URL rather than assumed — an image with no query at all would otherwise get '&width='
     and 404 the whole card's media. */
  function cdnWidth(src, w) {
    var base = String(src).split('&width=')[0];
    return base + (base.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  /* THE STRIPE NAME (D8-8, R239): the option line prints each value as the heart's own picker prints it: a colour input
     carries its printed name in data-svnty-name ("Blue stripe", snippets/product-variant-options.liquid); a size prints as it is */
  function optsLine(el, options) {
    var scope = (el.closest && (el.closest('quick-add-modal') || el.closest('section'))) || document;
    var named = scope.querySelectorAll('input[data-svnty-name], option[data-svnty-name]');
    return options.map(function (v) {
      for (var i = 0; i < named.length; i++) if (named[i].value === v) return named[i].getAttribute('data-svnty-name') || v;
      return v;
    }).join(' / ');
  }
  function applyVariant(el, variant) {
    if (!el || !variant || !variant.id) return;
    el.setAttribute('data-variant-id', String(variant.id));
    var opts = Array.isArray(variant.options) ? optsLine(el, variant.options) : '';
    /* a single-variant piece has one option called "Title" whose value is Shopify's own default —
       the picker never showed it, so it is never printed. The flag comes from Liquid, not a string
       comparison, because "Default Title" is translated. */
    if (el.getAttribute('data-single-variant') === 'true') opts = '';
    el.setAttribute('data-variant-opts', opts);
    el.setAttribute('data-variant-available', variant.available === false ? 'false' : 'true');
    if (variant.price != null) el.setAttribute('data-price', String(variant.price));
    el.setAttribute('data-compare', String(variant.compare_at_price || 0));
    var img = variant.featured_image && variant.featured_image.src;
    if (img) {
      el.setAttribute('data-image', cdnWidth(img, 400));
      el.setAttribute('data-image-2x', cdnWidth(img, 800));
    }
    var base = el.getAttribute('data-url') || '';
    if (base) el.setAttribute('data-url', base.split('?')[0] + '?variant=' + encodeURIComponent(variant.id));
  }
  /* THE COLOUR PIPELINE (D4-25): a colour whose size pair does not exist leaves Trade without a variant, so it sends no
     variantChange and the heart would keep the previous colour; then the heart takes the colour's first available variant
     (the bus names it in detail.variantId) straight from the picker's own variant data */
  function watchColourBus() {
    document.addEventListener('svnty:colour', function (e) {
      var d = (e && e.detail) || {};
      if (d.source === 'load' || !d.variantId) return;
      var sel = document.querySelector('section[id^="MainProduct-"] variant-selects');
      if (!sel || sel.currentVariant) return;              /* Trade resolved a variant: its own variantChange carries it */
      var data = [];
      try { data = typeof sel.getVariantData === 'function' ? sel.getVariantData() : []; } catch (err) { return; }
      var v = null;
      for (var i = 0; i < data.length; i++) { if (String(data[i].id) === String(d.variantId)) { v = data[i]; break; } }
      if (!v) return;
      document.querySelectorAll('svnty-wishlist-toggle.svnty-heart--pdp').forEach(function (el) {
        applyVariant(el, v);
        if (el.sync) el.sync();
      });
    });
  }
  /* D8-8 (found by the STRIPE NAME guard, W8): Trade's variantChange carries no event (global.js publishes { sectionId, html,
     variant }), so the size test below never saw a size pick, and a tap on the size Trade pre-checked fires no change at all:
     a heart on the product page saved no size and no option line unless the page arrived on ?variant=. The pick itself marks
     the hearts of its own picker, the door's inside the door, the page's outside it: a click on a size (the pre-checked one
     too, as the door's own no-size state reads it in svnty-pdp.js) or a change (a keyboard pick, a size dropdown). The sticky
     bar clicks the page's own radio, so it is heard here too. The heart's own attributes already name the checked variant. */
  function watchSizePick() {
    function mark(e) {
      var t = e.target;
      if (!t || !t.closest || !t.closest('variant-selects')) return;
      var input = t.closest('input[type="radio"], label, select');
      if (!input) return;
      var group = input.closest('[data-svnty-option]');
      var name = input.tagName === 'LABEL' ? '' : input.name || '';
      if (!/size/i.test(name) && !(group && /size/i.test(group.getAttribute('data-svnty-option') || ''))) return;
      var inDoor = !!t.closest('quick-add-modal');
      document.querySelectorAll('svnty-wishlist-toggle.svnty-heart--pdp').forEach(function (el) {
        if (!!(el.closest && el.closest('quick-add-modal')) === inDoor) el.setAttribute('data-svnty-size-picked', '1');
      });
    }
    document.addEventListener('click', mark, true);
    document.addEventListener('change', mark, true);
  }
  function watchVariantPicker() {
    try {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS !== 'object' || !PUB_SUB_EVENTS.variantChange) return;
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data && event.data.variant;
        if (!variant) return;
        /* D4-7: only a SIZE pick makes the size a choice; a colour pick keeps the default size and does not count */
        var src = event && event.data && event.data.event && event.data.event.target;
        var pickedSize = !!(src && ((/size/i.test(src.name || '')) || (src.closest && src.closest('.svnty-option--size'))));
        /* D5-3 (C-02): a pick inside a quick-add door speaks under the door's own section id (quickadd-...): it moves the
           door's heart only, never the product page's heart (which would then save the door's variant) */
        var fromDoor = String((event.data && event.data.sectionId) || '').indexOf('quickadd-') === 0;
        document.querySelectorAll('svnty-wishlist-toggle.svnty-heart--pdp').forEach(function (el) {
          if (!!(el.closest && el.closest('quick-add-modal')) !== fromDoor) return;
          applyVariant(el, variant);
          if (pickedSize) el.setAttribute('data-svnty-size-picked', '1');
          if (el.sync) el.sync();
        });
      });
    } catch (e) {
      /* global.js absent (a page without the picker): the server-rendered variant stands */
    }
  }

  if (!customElements.get('svnty-wishlist-toggle')) {
    customElements.define(
      'svnty-wishlist-toggle',
      class extends HTMLElement {
        connectedCallback() {
          this.key = this.getAttribute('data-product-handle') || this.getAttribute('data-product-id') || '';
          this.btn = this.querySelector('button');
          if (!this.btn) return;
          this.glyph();
          this.onClick = this.handleClick.bind(this);
          this.onChange = this.sync.bind(this);
          this.btn.addEventListener('click', this.onClick);
          document.addEventListener(EVT, this.onChange);
          this.sync();
        }
        disconnectedCallback() {
          if (this.btn) this.btn.removeEventListener('click', this.onClick);
          document.removeEventListener(EVT, this.onChange);
        }
        handleClick(e) {
          e.preventDefault();
          e.stopPropagation();
          var now = Date.now();
          if (this.lastTap && now - this.lastTap < 350) return; /* a double tap is one tap */
          this.lastTap = now;
          var was = this.btn.getAttribute('aria-pressed') === 'true';
          var saved = WL.toggle(readSnapshot(this));
          /* R2-34: the 200-item cap used to be a silent no-op — the heart did not move and nothing
             said why. It says why now, and the heart stays honest about the stored state. */
          if (saved === 'full') toast(STR.wl_full || '');
          var on = saved === true || WL.hasProduct(this.key);
          this.reflect(on);
          /* D6-11 THE HEART PAIR (R202): the tapped heart fills (THE POP) or empties (THE SETTLE) on the conductor, and the
             header's count is written in this same task, so the header's heart (and the menu bar's) joins the same stamp
             on the same first frame; the rAF announce then writes the same number and moves nothing. Other hearts of
             the same piece, other tabs and the page load stay still. */
          if (on !== was && window.SVNTY && window.SVNTY.pulse) window.SVNTY.pulse.play(this, 'heart', { up: on, fill: on ? 'in' : 'out', tick: false });
          updateCounts();
          if (saved === true) maybeNudge();
        }
        /* D6-11: a heart built by a script (the recently viewed rail) gets the same glyph the snippet renders */
        glyph() {
          if (this.btn.querySelector('.svnty-heart__glyph')) return;
          var a = this.btn.querySelector(':scope > .svnty-icon--heart'), b = this.btn.querySelector(':scope > .svnty-icon--heart-filled');
          if (!a || !b) return;
          var g = document.createElement('span');
          g.className = 'svnty-heart__glyph';
          g.setAttribute('aria-hidden', 'true');
          this.btn.insertBefore(g, a);
          g.appendChild(a);
          g.appendChild(b);
        }
        sync() {
          /* W-1: the PIECE, not the size — a shopper who saved an M must not see an empty heart
             when the picker moves to L, or she saves the same piece twice without knowing. */
          this.reflect(WL.hasProduct(this.key));
        }
        reflect(on) {
          this.classList.toggle('is-saved', !!on);
          if (!this.btn) return;
          /* R2-10: THE NAME NAMES THE PIECE. 24 cards on a collection page used to give a screen
             reader 24 buttons called "Save". The accessible name is now the ACTION the next press
             performs, with the product's own title in it, in both states; `aria-pressed` (which
             svnty-wishlist.css:30/33/105 keys the filled heart on) carries the state. The visible
             word on the PDP still reads Save / Saved. */
          var title = this.getAttribute('data-title') || '';
          this.btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          this.btn.setAttribute('aria-label', on ? fill('wl_remove', '[title]', title) : fill('wl_save_aria', '[title]', title));
          var label = this.querySelector('[data-svnty-heart-label]');
          /* a host may name the two words itself (the PDP's ADD TO WISHLIST pill, D3-11): data-label-save / data-label-saved */
          if (label) label.textContent = (on ? (this.getAttribute('data-label-saved') || STR.wl_saved) : (this.getAttribute('data-label-save') || STR.wl_save)) || '';
        }
      }
    );
  }

  /* ======================================================================= *
   * COUNTS — one selector, everywhere the mark appears
   * ======================================================================= */
  function updateCounts() {
    var n = WL.count();
    var label = String(Math.min(n, 99)); /* the bubble is a disc: two digits, never more */
    document.querySelectorAll('[data-svnty-wishlist-count]').forEach(function (el) {
      el.textContent = label;
      el.toggleAttribute('hidden', n === 0);
    });
    document.querySelectorAll('[data-svnty-wl-count]').forEach(function (el) {
      el.textContent = n ? '(' + n + ')' : '';
      el.toggleAttribute('hidden', n === 0);
    });
  }

  /* ======================================================================= *
   * THE PRODUCT FETCH: a share link's handles are checked against the store before they are saved
   * ======================================================================= */
  var products = {};
  var inflight = {};
  function fetchProduct(handle) {
    if (products[handle]) return Promise.resolve(products[handle]);
    if (inflight[handle]) return inflight[handle];
    function once() {
      var ctl = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = ctl
        ? window.setTimeout(function () {
            ctl.abort();
          }, FETCH_MS)
        : null;
      return fetch(root() + 'products/' + encodeURIComponent(handle) + '.js', {
        signal: ctl ? ctl.signal : undefined,
        credentials: 'same-origin'
      }).then(
        function (r) {
          if (timer) window.clearTimeout(timer);
          if (r.ok) return r.json();
          /* the STATUS rides on the error: 404/410 is a product that is gone, and nothing else is */
          var err = new Error('http ' + r.status);
          err.status = r.status;
          throw err;
        },
        function (err) {
          if (timer) window.clearTimeout(timer);
          throw err;
        }
      );
    }
    function gone(err) {
      return !!err && (err.status === 404 || err.status === 410);
    }
    /* one retry: a preview theme's first .js answer can be the cookie handshake, not JSON
       (the sibling sib-wishlist.js, owner's simulator 2026-08-27). A 404 is never retried — it is the
       answer, not a hiccup. */
    var p = once()
      .catch(function (err) {
        if (gone(err)) throw err;
        return once();
      })
      .then(function (prod) {
        if (!prod || !prod.variants) throw new Error('shape');
        products[handle] = prod;
        delete inflight[handle];
        return prod;
      })
      .catch(function (err) {
        delete inflight[handle];
        throw err;
      });
    inflight[handle] = p;
    return p;
  }


  /* ======================================================================= *
   * THE SHARE LINK — '?w=handle,handle' copied out, and merged in on load
   * ======================================================================= */
  function shareUrl() {
    var handles = WL.handles();
    if (!handles.length) return '';
    return location.origin + pageUrl() + '?w=' + handles.map(encodeURIComponent).join(',');
  }

  /* W-9: THE INSTRUCTION MUST POINT AT SOMETHING. When the clipboard refused, the shopper was told
     to "copy the link from the address bar" — but `?w=` is never in the address bar: shareUrl()
     only builds a string, and stripShareParam() deletes the parameter on load. The link is put on
     the page instead, in a readonly field, selected and ready for a manual copy. */
  function revealShareField(url) {
    /* P17-14 (copy audit P2-30): the locale key now says what the field shows, so the locale leads again and the
       page's own sentence is the fallback. */
    var manual = STR.wl_share_manual || readPageStrings().share_manual || '';
    var field = document.querySelector('[data-svnty-wl-share-field]');
    if (!field) {
      if (url) toast(manual);
      return;
    }
    if (!url) {
      field.hidden = true;
      return;
    }
    field.hidden = false;
    var input = field.querySelector('input');
    if (!input) return;
    input.value = url;
    try {
      input.focus({ preventScroll: false });
      input.setSelectionRange(0, input.value.length);
    } catch (e) {
      input.select();
    }
    toast(manual);
  }

  /* R2-6: THE PARAMETER IS SPENT THE MOMENT IT IS READ. `?w=` used to survive in the address bar and
     `mergeFromUrl()` runs on every init — so removing an imported piece and reloading brought it
     straight back, and the shopper could not get rid of it without editing the URL by hand. It is
     stripped with replaceState before a single request goes out, so a reload (and the back button,
     and a bookmark) sees the wishlist, not the import. */
  function stripShareParam() {
    try {
      var url = new URL(location.href);
      if (!url.searchParams.has('w')) return;
      url.searchParams.delete('w');
      var clean = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash;
      if (window.history && typeof window.history.replaceState === 'function') {
        window.history.replaceState(window.history.state, '', clean);
      }
    } catch (e) {}
  }

  /* R2-36: REJECT, NEVER MANGLE. The old sanitiser stripped every character outside [a-z0-9-], so
     `café-shirt` became `caf-shirt` — a handle that does not exist, counted in the toast and then
     kept for ever as a zero-price card. A candidate is either a plausible handle or it is dropped:
     no path separators, no scheme, no control characters, no query, nothing over 120 characters.
     Non-ASCII is ALLOWED, because a Shopify handle generated from an Arabic title contains it. */
  function candidateHandle(rawValue) {
    var value = str(rawValue).trim();
    try {
      value = decodeURIComponent(value);
    } catch (e) {
      return '';
    }
    value = value.trim().toLowerCase();
    if (!value || value.length > 120) return '';
    if (/[\s/\\:?#%&=<>"'\u0000-\u001f]/.test(value)) return '';
    return value;
  }

  function mergeFromUrl() {
    var raw;
    try {
      raw = new URL(location.href).searchParams.get('w');
    } catch (e) {
      return;
    }
    if (!raw) return;
    stripShareParam();

    var wanted = [];
    raw.split(',').forEach(function (piece) {
      var handle = candidateHandle(piece);
      if (!handle || WL.has(handle)) return;
      if (wanted.indexOf(handle) > -1) return;
      if (wanted.length >= MAX_IMPORT) return;
      wanted.push(handle);
    });
    if (!wanted.length) return;

    /* THE VERIFIED-SNAPSHOT LAW (R2-5): each handle is FETCHED, and only the product that comes
       back is stored. A handle that 404s, or that cannot be checked at all, is never turned into a
       card and never mentioned as if it had been saved. On the dev proxy, where /products/*.js
       answers 401, that means a share link imports nothing and says so — which is the honest
       outcome of not being able to verify. */
    var added = 0;
    var settled = 0;
    function finish() {
      settled++;
      if (settled < wanted.length) return;
      if (added > 0) toast(plural('wl_imported', added), { label: STR.wl_open || '', href: pageUrl() });
      else toast(STR.wl_imported_none || '');
    }
    wanted.forEach(function (handle) {
      fetchProduct(handle)
        .then(function (prod) {
          var result = WL.add({
            pid: prod.id,
            handle: prod.handle || handle,
            title: prod.title,
            image: prod.featured_image || '',
            price: prod.price,
            compare: prod.compare_at_price || 0,
            url: prod.url || '/products/' + (prod.handle || handle)
          });
          if (result === true) added++;
          else if (result === 'full') toast(STR.wl_full || '');
        })
        .catch(function () {})
        .then(finish);
    });
  }

  /* ======================================================================= *
   * DELEGATION — the page's own controls
   * ======================================================================= */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var share = t.closest('[data-svnty-wl-share]');
    if (share) {
      e.preventDefault();
      var url = shareUrl();
      if (!url) return;
      var ok = function () {
        revealShareField(null);
        toast(STR.wl_share_copied || '');
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(ok, function () {
          revealShareField(url);
        });
      } else {
        revealShareField(url);
      }
    }
  });

  /* ======================================================================= *
   * BOOT
   * ======================================================================= */
  function init() {
    readPageStrings();
    watchVariantPicker();
    watchColourBus();
    watchSizePick();
    mergeFromUrl();
    updateCounts();
  }
  document.addEventListener(EVT, updateCounts);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
